/**
 * ECS world definition, cloning and hashing. (§1.4)
 *
 * cloneWorld() and hashWorld() are the two functions the entire netcode rests
 * on. cloneWorld backs the prediction ring buffer (§2.3); hashWorld backs the
 * determinism test (§13.1) that guards every commit.
 *
 * ⚠ IF YOU ADD A FIELD TO WorldState, ADD IT TO BOTH FUNCTIONS BELOW.
 * A field that is simulated but not hashed makes the determinism test pass
 * while reconciliation silently rots — the exact failure §15.3 warns costs a
 * week to find. `assertWorldFieldsCovered` (bottom of this file) fails the
 * build if a new top-level key appears without being handled here. It cannot
 * catch a new field on a NESTED struct, so stay awake.
 */

import {
  type Attributes,
  type BallState,
  type MatchConfig,
  type MatchState,
  type OffsideSnapshot,
  type PlayerArrays,
  type Position,
  type Role,
  type SquadSize,
  type WorldState,
  MAX_PLAYERS,
  MatchPhase,
  SQUAD_PRESETS,
  createBallState,
  createMatchState,
  createOffsideSnapshot,
  createPlayerArrays,
} from './types.js';
import { pcg32CopyInto, pcg32Create } from '../math/pcg32.js';
import {
  AIR_DENSITY_SEA_LEVEL,
  ATTRIBUTE_NEUTRAL,
  DEFAULT_HALF_LENGTH_S,
  STAMINA_MAX,
  SUBSTITUTIONS_PER_TEAM,
  CURVE_ENABLED,
  STRIKE_AIM_ERROR_ENABLED,
} from './constants.js';

// ---------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------

/** A neutral 50-across attribute set. Every stat at the 1.0× midpoint. (§8.1) */
export function createNeutralAttributes(): Attributes {
  const n = ATTRIBUTE_NEUTRAL;
  return {
    pace: n, acceleration: n, stamina: n, strength: n, agility: n, balance: n, jumping: n,
    passing: n, shooting: n, dribbling: n, firstTouch: n, crossing: n, finishing: n,
    longShots: n, freeKick: n, penalties: n, heading: n,
    vision: n, composure: n, positioning: n, reactions: n, aggression: n, workRate: n,
    tackling: n, marking: n, interceptions: n, standingTackle: n, slidingTackle: n,
    reflexes: n, handling: n, diving: n, kicking: n, gkPositioning: n, oneOnOne: n,
  };
}

export function createMatchConfig(
  squadSize: SquadSize,
  overrides: Partial<MatchConfig> = {},
): MatchConfig {
  const preset = SQUAD_PRESETS[squadSize];
  return {
    squadSize,
    pitchLength: preset.pitchLength,
    pitchWidth: preset.pitchWidth,
    hasGoalkeepers: preset.hasGoalkeepers,
    offsideEnabled: preset.offsideEnabled,
    foulsEnabled: true,
    halfLengthS: DEFAULT_HALF_LENGTH_S,
    airDensity: AIR_DENSITY_SEA_LEVEL,
    wetPitch: false,
    seed: 1,
    varEnabled: false,
    substitutionsPerTeam: SUBSTITUTIONS_PER_TEAM,
    shootoutOnDraw: true,
    // §4 KICK DIRECTIVE (constants.ts): production takes the shipped defaults, so
    // the owner's two booleans remain the single place either mechanic is decided.
    // Only the curve/strike-error suites override them, to test the ENABLED path.
    curveEnabled: CURVE_ENABLED,
    strikeAimErrorEnabled: STRIKE_AIM_ERROR_ENABLED,
    ...overrides,
  };
}

export function createWorld(config: MatchConfig): WorldState {
  const attributes: Attributes[] = new Array(MAX_PLAYERS);
  const positions: Position[] = new Array(MAX_PLAYERS);
  const roles: Role[] = new Array(MAX_PLAYERS);
  for (let i = 0; i < MAX_PLAYERS; i++) {
    attributes[i] = createNeutralAttributes();
    positions[i] = 'CM';
    roles[i] = 'Standard';
  }

  const players = createPlayerArrays();
  players.stamina.fill(STAMINA_MAX);
  players.gkHoldStartTick.fill(-1);

  return {
    tick: 0,
    config,
    players,
    ball: createBallState(),
    match: createMatchState(),
    offside: createOffsideSnapshot(),
    // Each match gets its own PCG stream, so two matches in one server process
    // can never correlate their random draws. (§15.3)
    rng: pcg32Create(config.seed, config.seed),
    attributes,
    positions,
    roles,
    teamAAttacksPositiveX: true,
    contactTiming: false,
  };
}

// ---------------------------------------------------------------------------
// Cloning — buffer copies only. Zero garbage. (§15.7)
// ---------------------------------------------------------------------------

/**
 * Allocate a world shaped like `src`, for use as a ring-buffer slot.
 * Call this INPUT_BUFFER_CAPACITY times at startup, then only ever
 * cloneWorldInto() at runtime.
 */
export function allocWorldLike(src: WorldState): WorldState {
  const w = createWorld({ ...src.config });
  cloneWorldInto(w, src);
  return w;
}

/** Convenience clone. Allocates — do NOT call in the game loop. */
export function cloneWorld(src: WorldState): WorldState {
  return allocWorldLike(src);
}

/**
 * Copy `src` over `dst` with zero allocation. This is the hot path: it runs
 * once per predicted tick (§2.3) and on every reconciliation rewind (§2.4).
 */
export function cloneWorldInto(dst: WorldState, src: WorldState): void {
  dst.tick = src.tick;
  dst.teamAAttacksPositiveX = src.teamAAttacksPositiveX;
  dst.contactTiming = src.contactTiming;

  copyMatchConfig(dst.config, src.config);
  copyPlayerArrays(dst.players, src.players);
  copyBallState(dst.ball, src.ball);
  copyMatchState(dst.match, src.match);
  copyOffsideSnapshot(dst.offside, src.offside);
  pcg32CopyInto(dst.rng, src.rng);

  // Attributes/positions/roles are static for the whole match. Copying them on
  // every rewind would be pure waste, so we share the references and rely on
  // nothing mutating them mid-match. `freezeStaticData` enforces that in dev.
  dst.attributes = src.attributes;
  dst.positions = src.positions;
  dst.roles = src.roles;
}

/**
 * Copy one MatchConfig over another. Exported so the roster round-trip can be
 * asserted directly (sim/roster.test.ts) rather than only inferred through a
 * whole-world clone — this function is where a forgotten field turns into a
 * desync, so it is worth being able to point a test at it.
 */
export function copyMatchConfig(dst: MatchConfig, src: MatchConfig): void {
  dst.squadSize = src.squadSize;
  dst.pitchLength = src.pitchLength;
  dst.pitchWidth = src.pitchWidth;
  dst.hasGoalkeepers = src.hasGoalkeepers;
  dst.offsideEnabled = src.offsideEnabled;
  dst.foulsEnabled = src.foulsEnabled;
  dst.halfLengthS = src.halfLengthS;
  dst.airDensity = src.airDensity;
  dst.wetPitch = src.wetPitch;
  dst.seed = src.seed;
  dst.varEnabled = src.varEnabled;
  dst.shootoutOnDraw = src.shootoutOnDraw;
  dst.substitutionsPerTeam = src.substitutionsPerTeam;
  // Miss either of these and a rewind would silently re-enable/disable the gate
  // mid-match — i.e. reconciliation would strike a different ball than prediction.
  dst.curveEnabled = src.curveEnabled;
  dst.strikeAimErrorEnabled = src.strikeAimErrorEnabled;
  // THE CHOSEN XI (types.ts MatchConfig.roster). Miss this and a rewind rebuilds
  // a DIFFERENT eleven than prediction did — every re-simulated tick then runs
  // the wrong men on the wrong positions, which is a desync, not a glitch.
  //
  // Shared BY REFERENCE, deliberately, exactly like attributes/positions/roles
  // above and for the same two reasons: the roster is immutable for the whole
  // match (readonly arrays, decided before kickoff, never written by a tick), and
  // this function is the hot path — it runs once per predicted tick and on every
  // reconciliation rewind (§15.7 zero-allocation). Deep-copying eleven strings
  // twice per tick would be pure garbage for a value that cannot change.
  // `exactOptionalPropertyTypes` forbids writing `undefined` into an optional
  // property, and an ABSENT roster is the compatibility case that must reproduce
  // today's kickoff exactly — so delete rather than assign undefined.
  if (src.roster !== undefined) dst.roster = src.roster;
  else delete dst.roster;
}

function copyPlayerArrays(dst: PlayerArrays, src: PlayerArrays): void {
  dst.active.set(src.active);
  dst.team.set(src.team);
  dst.px.set(src.px);
  dst.py.set(src.py);
  dst.pz.set(src.pz);
  dst.vx.set(src.vx);
  dst.vy.set(src.vy);
  dst.vz.set(src.vz);
  dst.yaw.set(src.yaw);
  dst.stamina.set(src.stamina);
  dst.anim.set(src.anim);
  dst.animExecId.set(src.animExecId);
  dst.lastMoveTick.set(src.lastMoveTick);
  dst.animEnterTick.set(src.animEnterTick);
  dst.lastGaitTick.set(src.lastGaitTick);
  dst.lastBand.set(src.lastBand);
  dst.prevHeading.set(src.prevHeading);
  dst.gkHoldTapTick.set(src.gkHoldTapTick);
  dst.gkHoldTapPrev.set(src.gkHoldTapPrev);
  dst.animBlend.set(src.animBlend);
  dst.actionTimer.set(src.actionTimer);
  dst.skillActive.set(src.skillActive);
  dst.skillTimer.set(src.skillTimer);
  dst.yellows.set(src.yellows);
  dst.sentOff.set(src.sentOff);
  dst.possessing.set(src.possessing);
  dst.gkHoldStartTick.set(src.gkHoldStartTick);
  dst.passCharge.set(src.passCharge);
  dst.shootCharge.set(src.shootCharge);
  dst.knockdownTimer.set(src.knockdownTimer);
  dst.stumbleTimer.set(src.stumbleTimer);
  dst.punchKickOffenses.set(src.punchKickOffenses);
  dst.actKind.set(src.actKind);
  dst.actStartTick.set(src.actStartTick);
  dst.actExecId.set(src.actExecId);
  dst.actContacted.set(src.actContacted);
  dst.actAimYaw.set(src.actAimYaw);
  dst.actPower.set(src.actPower);
  dst.actCurl.set(src.actCurl);
  dst.actChip.set(src.actChip);
  dst.actFoot.set(src.actFoot);
  dst.actTarget.set(src.actTarget);
}

function copyBallState(dst: BallState, src: BallState): void {
  dst.px = src.px; dst.py = src.py; dst.pz = src.pz;
  dst.vx = src.vx; dst.vy = src.vy; dst.vz = src.vz;
  dst.wx = src.wx; dst.wy = src.wy; dst.wz = src.wz;
  dst.qx = src.qx; dst.qy = src.qy; dst.qz = src.qz; dst.qw = src.qw;
  dst.lastTouchedBy = src.lastTouchedBy;
  dst.lastTouchedTeam = src.lastTouchedTeam;
  dst.lastTouchTick = src.lastTouchTick;
  dst.possessedBy = src.possessedBy;
  dst.gatherOwner = src.gatherOwner;
  dst.controlState = src.controlState;
  dst.contestTicks = src.contestTicks;
  dst.grounded = src.grounded;
  dst.powerKick = src.powerKick;
}

function copyMatchState(dst: MatchState, src: MatchState): void {
  dst.phase = src.phase;
  dst.restartStage = src.restartStage;
  dst.stageTimer = src.stageTimer;
  dst.half = src.half;
  dst.clockTicks = src.clockTicks;
  dst.addedTimeTicks = src.addedTimeTicks;
  dst.scoreA = src.scoreA;
  dst.scoreB = src.scoreB;
  dst.restartTeam = src.restartTeam;
  dst.restartX = src.restartX;
  dst.restartZ = src.restartZ;
  dst.restartTaker = src.restartTaker;
  dst.possessionTeam = src.possessionTeam;
  dst.restartKickPhase = src.restartKickPhase;
  dst.advantageTicks = src.advantageTicks;
  dst.pendingFoulPlayer = src.pendingFoulPlayer;
  dst.pendingFoulSeverity = src.pendingFoulSeverity;
  dst.pendingWhistleTicks = src.pendingWhistleTicks;
  dst.pendingWhistleFouler = src.pendingWhistleFouler;
  dst.pendingWhistleTeam = src.pendingWhistleTeam;
  dst.pendingWhistleX = src.pendingWhistleX;
  dst.pendingWhistleZ = src.pendingWhistleZ;
  dst.excitement = src.excitement;
  dst.shootoutActive = src.shootoutActive;
  dst.shootoutKicksA = src.shootoutKicksA;
  dst.shootoutKicksB = src.shootoutKicksB;
  dst.shootoutGoalsA = src.shootoutGoalsA;
  dst.shootoutGoalsB = src.shootoutGoalsB;
  dst.shootoutFirstTeam = src.shootoutFirstTeam;
  dst.shootoutAttemptTicks = src.shootoutAttemptTicks;
}

function copyOffsideSnapshot(dst: OffsideSnapshot, src: OffsideSnapshot): void {
  dst.tick = src.tick;
  dst.team = src.team;
  dst.passer = src.passer;
  dst.offsidePosition.set(src.offsidePosition);
  dst.lineCoord = src.lineCoord;
  dst.fromRestart = src.fromRestart;
}

// ---------------------------------------------------------------------------
// Hashing — FNV-1a over the dynamic state. (§13.1)
// ---------------------------------------------------------------------------

const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/** Scratch view for reinterpreting an f64 as two u32s. Module-scope: no alloc. */
const f64Scratch = new Float64Array(1);
const u32View = new Uint32Array(f64Scratch.buffer);

function hashU32(h: number, v: number): number {
  h ^= v & 0xff;
  h = Math.imul(h, FNV_PRIME);
  h ^= (v >>> 8) & 0xff;
  h = Math.imul(h, FNV_PRIME);
  h ^= (v >>> 16) & 0xff;
  h = Math.imul(h, FNV_PRIME);
  h ^= (v >>> 24) & 0xff;
  h = Math.imul(h, FNV_PRIME);
  return h >>> 0;
}

/**
 * Hash an f64 by its exact bit pattern, not its decimal value.
 *
 * This is the whole point of the determinism test: two runs that differ by one
 * ulp MUST produce different hashes. Hashing `v.toString()` or rounding first
 * would mask exactly the drift we are trying to catch.
 *
 * -0 is normalized to +0 first: they are `===` but have different bit patterns,
 * and a velocity legitimately reaching -0 instead of +0 is not a desync.
 * NaN is normalized to a canonical quiet NaN for the same reason.
 */
function hashF64(h: number, v: number): number {
  let x = v;
  if (x === 0) x = 0; // collapse -0 → +0
  else if (Number.isNaN(x)) x = Number.NaN; // canonical NaN
  f64Scratch[0] = x;
  h = hashU32(h, u32View[0]!);
  h = hashU32(h, u32View[1]!);
  return h;
}

function hashF64Array(h: number, arr: Float64Array): number {
  for (let i = 0; i < arr.length; i++) h = hashF64(h, arr[i]!);
  return h;
}

function hashIntArray(h: number, arr: Uint8Array | Int8Array | Int32Array): number {
  for (let i = 0; i < arr.length; i++) h = hashU32(h, arr[i]! >>> 0);
  return h;
}

/**
 * Stable hash of the DYNAMIC simulation state.
 *
 * Excludes: config, attributes, positions, roles — all static for the match.
 * Two sims with different configs are different sims, not a desync; the
 * determinism test seeds both sides identically.
 *
 * Returns a uint32. That is ~4×10⁹ buckets, so an accidental collision masking
 * a real desync across a 10,000-tick test has probability ~2e-6. Acceptable for
 * CI; if that ever feels tight, hash to two independent u32s rather than
 * widening this one.
 */
export function hashWorld(w: WorldState): number {
  let h = FNV_OFFSET_BASIS;

  h = hashU32(h, w.tick >>> 0);
  h = hashU32(h, w.teamAAttacksPositiveX ? 1 : 0);
  h = hashU32(h, w.contactTiming ? 1 : 0);

  // RNG state — if this diverges, every subsequent draw diverges.
  h = hashU32(h, w.rng.stateHi);
  h = hashU32(h, w.rng.stateLo);
  h = hashU32(h, w.rng.incHi);
  h = hashU32(h, w.rng.incLo);

  const p = w.players;
  h = hashIntArray(h, p.active);
  h = hashIntArray(h, p.team);
  h = hashF64Array(h, p.px);
  h = hashF64Array(h, p.py);
  h = hashF64Array(h, p.pz);
  h = hashF64Array(h, p.vx);
  h = hashF64Array(h, p.vy);
  h = hashF64Array(h, p.vz);
  h = hashF64Array(h, p.yaw);
  h = hashF64Array(h, p.stamina);
  h = hashIntArray(h, p.anim);
  h = hashIntArray(h, p.animExecId);
  h = hashIntArray(h, p.lastMoveTick);
  h = hashIntArray(h, p.animEnterTick);
  h = hashIntArray(h, p.lastGaitTick);
  h = hashIntArray(h, p.lastBand);
  h = hashF64Array(h, p.prevHeading);
  h = hashIntArray(h, p.gkHoldTapTick);
  h = hashIntArray(h, p.gkHoldTapPrev);
  h = hashF64Array(h, p.animBlend);
  h = hashF64Array(h, p.actionTimer);
  h = hashIntArray(h, p.skillActive);
  h = hashF64Array(h, p.skillTimer);
  h = hashIntArray(h, p.yellows);
  h = hashIntArray(h, p.sentOff);
  h = hashIntArray(h, p.possessing);
  h = hashIntArray(h, p.gkHoldStartTick);
  h = hashF64Array(h, p.passCharge);
  h = hashF64Array(h, p.shootCharge);
  h = hashF64Array(h, p.knockdownTimer);
  h = hashF64Array(h, p.stumbleTimer);
  h = hashIntArray(h, p.punchKickOffenses);
  h = hashIntArray(h, p.actKind);
  h = hashIntArray(h, p.actStartTick);
  h = hashIntArray(h, p.actExecId);
  h = hashIntArray(h, p.actContacted);
  h = hashF64Array(h, p.actAimYaw);
  h = hashF64Array(h, p.actPower);
  h = hashF64Array(h, p.actCurl);
  h = hashIntArray(h, p.actChip);
  h = hashIntArray(h, p.actFoot);
  h = hashIntArray(h, p.actTarget);

  const b = w.ball;
  h = hashF64(h, b.px); h = hashF64(h, b.py); h = hashF64(h, b.pz);
  h = hashF64(h, b.vx); h = hashF64(h, b.vy); h = hashF64(h, b.vz);
  h = hashF64(h, b.wx); h = hashF64(h, b.wy); h = hashF64(h, b.wz);
  h = hashF64(h, b.qx); h = hashF64(h, b.qy); h = hashF64(h, b.qz); h = hashF64(h, b.qw);
  h = hashU32(h, b.lastTouchedBy >>> 0);
  h = hashU32(h, b.lastTouchedTeam >>> 0);
  h = hashU32(h, b.lastTouchTick >>> 0);
  h = hashU32(h, b.possessedBy >>> 0);
  // Derived, but hashed: a peer that derives a different contest from
  // identical inputs has a desync, and it surfaces here rather than later.
  h = hashU32(h, b.controlState >>> 0);
  h = hashU32(h, b.contestTicks >>> 0);
  h = hashU32(h, b.gatherOwner >>> 0);
  h = hashU32(h, b.grounded >>> 0);
  h = hashU32(h, b.powerKick ? 1 : 0);

  const m = w.match;
  h = hashU32(h, m.phase >>> 0);
  h = hashU32(h, m.restartStage >>> 0);
  h = hashF64(h, m.stageTimer);
  h = hashU32(h, m.half >>> 0);
  h = hashU32(h, m.clockTicks >>> 0);
  h = hashU32(h, m.addedTimeTicks >>> 0);
  h = hashU32(h, m.scoreA >>> 0);
  h = hashU32(h, m.scoreB >>> 0);
  h = hashU32(h, m.restartTeam >>> 0);
  h = hashF64(h, m.restartX);
  h = hashF64(h, m.restartZ);
  h = hashU32(h, m.restartTaker >>> 0);
  h = hashU32(h, m.possessionTeam >>> 0);
  h = hashU32(h, m.restartKickPhase >>> 0);
  h = hashU32(h, m.advantageTicks >>> 0);
  h = hashU32(h, m.pendingFoulPlayer >>> 0);
  h = hashU32(h, m.pendingFoulSeverity >>> 0);
  h = hashU32(h, m.pendingWhistleTicks >>> 0);
  h = hashU32(h, m.pendingWhistleFouler >>> 0);
  h = hashU32(h, m.pendingWhistleTeam >>> 0);
  h = hashF64(h, m.pendingWhistleX);
  h = hashF64(h, m.pendingWhistleZ);
  h = hashF64(h, m.excitement);
  // The shootout decides who WON — a peer that disagrees about the tally
  // disagrees about the match result, so every counter is hashed.
  h = hashU32(h, m.shootoutActive >>> 0);
  h = hashU32(h, m.shootoutKicksA >>> 0);
  h = hashU32(h, m.shootoutKicksB >>> 0);
  h = hashU32(h, m.shootoutGoalsA >>> 0);
  h = hashU32(h, m.shootoutGoalsB >>> 0);
  h = hashU32(h, m.shootoutFirstTeam >>> 0);
  h = hashU32(h, m.shootoutAttemptTicks >>> 0);

  const o = w.offside;
  h = hashU32(h, o.tick >>> 0);
  h = hashU32(h, o.team >>> 0);
  h = hashU32(h, o.passer >>> 0);
  h = hashIntArray(h, o.offsidePosition);
  h = hashF64(h, o.lineCoord);
  h = hashU32(h, o.fromRestart >>> 0);

  return h >>> 0;
}

/** Hex form, for CI logs and desync reports. */
export function hashWorldHex(w: WorldState): string {
  return hashWorld(w).toString(16).padStart(8, '0');
}

// ---------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------

/**
 * Every top-level WorldState key that clone/hash know about.
 * `assertWorldFieldsCovered` compares this against the real object at runtime,
 * so adding a field to the interface without touching this file fails loudly
 * in dev instead of silently in production three weeks later.
 */
const KNOWN_WORLD_KEYS: ReadonlyArray<keyof WorldState> = [
  'tick', 'config', 'players', 'ball', 'match', 'offside', 'rng',
  'attributes', 'positions', 'roles', 'teamAAttacksPositiveX', 'contactTiming',
];

export function assertWorldFieldsCovered(w: WorldState): void {
  const actual = Object.keys(w).sort();
  const known = [...KNOWN_WORLD_KEYS].sort();
  const missing = actual.filter((k) => !known.includes(k as keyof WorldState));
  if (missing.length > 0) {
    throw new Error(
      `WorldState has field(s) not handled by cloneWorldInto/hashWorld: ${missing.join(', ')}.\n` +
      'Add them to BOTH functions in world.ts and to KNOWN_WORLD_KEYS, or the ' +
      'determinism test will pass while reconciliation silently rots (§15.3).',
    );
  }
}

// ---------------------------------------------------------------------------
// Player slot helpers
// ---------------------------------------------------------------------------

export function spawnPlayer(
  w: WorldState,
  id: number,
  team: 0 | 1,
  position: Position,
  role: Role = 'Standard',
  attributes?: Attributes,
): void {
  const p = w.players;
  p.active[id] = 1;
  p.team[id] = team;
  p.stamina[id] = STAMINA_MAX;
  p.gkHoldStartTick[id] = -1;
  // Reset the contact-action state so a re-used slot never inherits a stale action.
  p.actKind[id] = 0;
  p.actStartTick[id] = -1;
  p.actContacted[id] = 0;
  p.actTarget[id] = -1;
  w.positions[id] = position;
  w.roles[id] = role;
  if (attributes) w.attributes[id] = attributes;
}

/** Iterate active, not-sent-off players. Returns count written into `out`. */
export function activePlayers(w: WorldState, out: Int32Array): number {
  let n = 0;
  const p = w.players;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] === 1 && p.sentOff[i] === 0) out[n++] = i;
  }
  return n;
}

/** True once the match is over and no further ticks should be simulated. */
export function isMatchOver(w: WorldState): boolean {
  return w.match.phase === MatchPhase.FullTime;
}
